import { DateTime } from "luxon";
import type { ScheduleChangeItem, ScheduleChangeItemCached, ScheduleChangeItemWire, WireDateTime } from "./types";

/**
 * One wire datetime in the department's zone. Throws on anything Luxon can't read: a change with an unreadable time
 * cannot be placed on a day, and dropping it quietly would hide a change the scheduler came here to see.
 */
export const toDepartmentDateTime = (value: WireDateTime, timezone: string, field: string): DateTime => {
	const parsed = typeof value === "string" ? DateTime.fromISO(value, { setZone: true }) : value;
	const zoned = parsed.setZone(timezone);
	if (!zoned.isValid) {
		throw new Error(`schedule changes: unreadable ${field} "${String(value)}" (${zoned.invalidExplanation})`);
	}
	return zoned;
};

const toOptionalDepartmentDateTime = (value: WireDateTime | null, timezone: string, field: string): DateTime | null =>
	value === null ? null : toDepartmentDateTime(value, timezone, field);

export const parseScheduleChangeItem = (wire: ScheduleChangeItemWire, timezone: string): ScheduleChangeItem => ({
	...wire,
	changedAt: toDepartmentDateTime(wire.changedAt, timezone, "changedAt"),
	shiftStart: toOptionalDepartmentDateTime(wire.shiftStart, timezone, "shiftStart"),
	shiftEnd: toOptionalDepartmentDateTime(wire.shiftEnd, timezone, "shiftEnd"),
});

/**
 * A wire datetime back to the ISO string the server sent — what the query cache holds (see `ScheduleChangeItemCached`).
 * Throws on an unreadable value for the same reason `toDepartmentDateTime` does.
 */
export const toWireIso = (value: WireDateTime, field: string): string => {
	if (typeof value === "string") return value;
	const iso = value.toISO();
	if (iso === null) throw new Error(`schedule changes: unreadable ${field} (${value.invalidExplanation})`);
	return iso;
};

const toOptionalWireIso = (value: WireDateTime | null, field: string): string | null => (value === null ? null : toWireIso(value, field));

export const toCachedScheduleChangeItem = (wire: ScheduleChangeItemWire): ScheduleChangeItemCached => ({
	...wire,
	changedAt: toWireIso(wire.changedAt, "changedAt"),
	shiftStart: toOptionalWireIso(wire.shiftStart, "shiftStart"),
	shiftEnd: toOptionalWireIso(wire.shiftEnd, "shiftEnd"),
});
